<script lang="ts" setup>
import { motion } from 'motion-v';

interface Props {
  message: string;
  align?: 'left' | 'right' | 'center';
  bubblePosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

const props = withDefaults(defineProps<Props>(), {
  align: 'center',
  bubblePosition: 'bottom-right',
});

const containerClass = computed(() => {
  return twMerge(
    cva('flex', {
      variants: {
        align: {
          center: 'justify-center',
          right: 'justify-end',
          left: 'justify-start',
        },
      },
    })({
      align: props.align,
    }),
  );
});
const blinkyClass = computed(() => {
  return twMerge(
    cva('', {
      variants: {
        bubblePosition: {
          'top-right': 'order-first -ml-4 self-end',
          'top-left': 'order-last self-end',
          'bottom-right': 'order-first -mr-4',
          'bottom-left': 'order-last -ml-4',
        },
      },
    })({
      bubblePosition: props.bubblePosition,
    }),
  );
});
const bubbleClass = computed(() => {
  return twMerge(
    cva('font-medium sm:text-lg', {
      variants: {
        bubblePosition: {
          'top-right': 'mb-24 -ml-4',
          'top-left': 'mb-24 -mr-4',
          'bottom-right': 'mt-24',
          'bottom-left': 'mt-24',
        },
      },
    })({
      bubblePosition: props.bubblePosition,
    }),
  );
});

const bubbleTailDirection = computed(() => {
  // inverts the position to point the bubble's tail back at Blinky:
  switch (props.bubblePosition) {
    case 'top-left':
      return 'bottom-right';
    case 'top-right':
      return 'bottom-left';
    case 'bottom-left':
      return 'top-right';
    case 'bottom-right':
      return 'top-left';
    default:
      return 'bottom-right';
  }
});

const initialBubbleY = computed(() => (props.bubblePosition.startsWith('top') ? -40 : 40));
</script>

<template>
  <div :class="containerClass">
    <KUIUnifiedBlinky
      size="xl"
      :class="blinkyClass"
    />
    <motion.div
      :initial="{ opacity: 0, y: initialBubbleY }"
      :while-in-view="{ opacity: 1, y: 0 }"
      :in-view-options="{ once: false, amount: 0.8 }"
      :transition="{ duration: 0.6, ease: 'easeOut' }"
      class="z-1"
    >
      <KUIUnifiedMessageBubble
        role="assistant"
        :message="message"
        :loading="false"
        :direction="bubbleTailDirection"
        :class="bubbleClass"
      />
    </motion.div>
  </div>
</template>
